// ==============================================================================================
//  APPLICATION AUTHORSHIP & RIGHTS NOTICE
//
//  Developed by: Gareth Morgan
//  Brand & Product: BuildMyChecks.co.uk
//  Ownership: Sole author and rights holder
//
//  ----------------------------------------------------------------------------------------------
//  NOTICE:
//  - This application and its related systems were designed, developed, and built solely
//    by Gareth Morgan.
//  - Gareth Morgan retains full authorship, intellectual property, and creative rights to
//    all Build My Checks branding, technology, source code, architecture, and design.
//  - No ownership, license, or implied rights are granted to any third party unless
//    explicitly agreed in writing by Gareth Morgan.
//  - Gareth Morgan is NOT responsible for future maintenance, updates, security patching,
//    or operational upkeep if this application is deployed, copied, or modified without
//    his written consent.
//  - Gareth Morgan reserves the right to:
//       • Withdraw or disable access to the application or related services
//       • Remove hosted, cloud-based, or linked services under his control
//       • Revoke access to Build My Checks–related assets, code, APIs, or integrations
//
//  © 2025 Gareth Morgan — BuildMyChecks.co.uk
//  Unauthorized use, alteration, copying, or redistribution without written consent
//  is strictly prohibited.
//
//  ----------------------------------------------------------------------------------------------
//  Build My Checks – ASCII Logo Signature
//  (Symbolic representation of BuildMyChecks.co.uk identity)
//
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................:-==++-.........:-:.............................................
// ...................................-*******:.......=*****+-.........................................
// ...................................=*******+:.....+*******=.........................................
// .........................::........+**********************=.........................................
// .......................-+***+-.:-++***********************=:.......:................................
// .....................-+*************************************+-..=+**+-..............................
// .....................:+*************+=-.............-++***************+:............................
// ......................:+********+=:.....................:=*************=............................
// .......................=******+:...........................-+********+-.............................
// ......................-*****+:...............................-+*****+-..............................
// ................:::::+****+-...................................=*****+..............................
// ...............+-......:=+-........+=.*%=......##:.:::.....:::..-*****=.............................
// ..............=*-*@%%%%@*.........-#*:#@=......@%::@@@*...*@@#...-+++++**++=-.......................
// .............:**-*@*..=@%.=#+..*#=-%#:#@=.=#%#+@%::@%%%-.=@%@#-##:..##:+*****:......................
// ..............-+-*@@%%@%+.+@*..#@==%#:#@==@%-:+@%::@#-@#:%#+%#.+@#.*@+-******=......................
// .................*@*..:#@++@*..#@==%#:#@=+@#...@%::@#.+@%%-=%#..%@#%#:+******+......................
// .................*@%##%@#.-%@%%@@==%#:#@=:#@%#%%%::@#.:#@*.=%#...%@%:=****+-:.......................
// ..................::........::.........:....::.....:....:......:-#@--***+:..........................
// ................:++:+@@@@@*.:@#:.....................#%=......:#%#-.+***+:..........................
// .............-+**+:#@*:.:+%+:@#-=+-...:=+=-....-+=-..#%=.:=-..-++-..+***+:..........................
// .............+***=-@%.......:@@#+#@#.+@#-+%#:+@%++@%:#%+*@#::%#--*-:*****+-.........................
// .............-***=:@%.......:@%:.-%%:%@%%%%%+#@=.....#@@@%:..#%@@%-=**+++++*+.......................
// ..............****-+%@*=+%@*:@%:.-%%.*@*-=*=.*@%==%#:#@+=@%=:++-+@*-+--===:+=.......................
// .................::::=***=:..++..:++..-+**=...:+**+:.=+-.:++:-*#*=::-:+***=::-++:...................
// ....................-****+=............................*#......+*****************=..................
// .....................=*****=.......+##+.:+##=....*+.-#-*#.**:..:+************++*=...................
// .....................+******+-....*%..:.%*.:%+..:%*.=%=*%%*..:--***********+:..-+:..................
// ....................+*********=:%=+%*+%-*%+*%-=#-#%*%@=*#:%*:+****+++****+=...=*****-...............
// ...................=**********+--::.::....::...:..::.:.:-::-:=***+:..=+*+:..:+******-...............
// ....................-******++*******+=-:............-=++****+=+***+:...:...=******+-:...............
// ......................-++-....=+***************************+=+-=****+:...-+******-..................
// ................................=**********************++-:....=******=:+********=..................
// ................................=**********************=......:+*****************+:.................
// ................................+*******=......+*******=........-+=.:=+****+-.-+-...................
// ................................:-=+***-........+******=..............=***+.........................
// .................................................--::...............................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
//
// ==============================================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SplashScreen from '../../components/SplashScreen';
import InteractiveDemo from '../../components/InteractiveDemo';
import Navbar from '../../components/Navbar';
import BuildMyChecksSocialLinks from '../../components/BuildMyChecksSocialLinks';
import { useTranslation } from 'react-i18next';
import cogzImage from '../../assets/cogz.png';

export default function LandingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showSplash, setShowSplash] = useState(true);
  const [activeBlogTab, setActiveBlogTab] = useState('compliance');
  const { isIOS } = (() => {
    if (typeof window === 'undefined') return { isIOS: false, isAndroid: false };
    const params = new URLSearchParams(window.location.search || '');
    const forcedIOS = params.get('platform') === 'ios';
    const forcedAndroid = params.get('platform') === 'android';
    const ua = (window.navigator?.userAgent || '').toLowerCase();
    const platform = (window.navigator?.platform || '').toLowerCase();
    const touchCapable = typeof window.navigator?.maxTouchPoints === 'number' && window.navigator.maxTouchPoints > 1;
    const isIOSDetected =
      forcedIOS ||
      /iphone|ipad|ipod|safariviewservice/.test(ua) ||
      ((platform === 'macintel' || ua.includes('mac os x')) && touchCapable);
    const isAndroidDetected = forcedAndroid || /android/.test(ua);
    return { isIOS: isIOSDetected, isAndroid: isAndroidDetected };
  })();

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'BuildMyChecks',
    url: 'https://buildmychecks.com',
    logo: 'https://buildmychecks.com/logo.png',
    description:
      'BuildMyChecks provides digital inspection and compliance checklist software for businesses, helping teams standardise inspections and stay audit-ready.',
    sameAs: [
      'https://www.linkedin.com/company/buildmychecks/',
      'https://www.instagram.com/buildmychecks/',
      'https://www.youtube.com/@buildmychecks',
    ],
  };

  const blogTabs = [
    {
      id: 'compliance',
      title: t('subscription.blogComplianceTitle'),
      heading: t('subscription.blogComplianceHeading'),
      summary: t('subscription.blogComplianceSummary'),
      bullets: [
        t('subscription.blogComplianceBullet1'),
        t('subscription.blogComplianceBullet2'),
        t('subscription.blogComplianceBullet3'),
      ],
      href: '/compliance-checklist-software-uk',
      cta: t('subscription.blogComplianceCta'),
    },
    {
      id: 'safety',
      title: t('subscription.blogSafetyTitle'),
      heading: t('subscription.blogSafetyHeading'),
      summary: t('subscription.blogSafetySummary'),
      bullets: [
        t('subscription.blogSafetyBullet1'),
        t('subscription.blogSafetyBullet2'),
        t('subscription.blogSafetyBullet3'),
      ],
      href: '/health-and-safety-inspection-software',
      cta: t('subscription.blogSafetyCta'),
    },
    {
      id: 'digital',
      title: t('subscription.blogDigitalTitle'),
      heading: t('subscription.blogDigitalHeading'),
      summary: t('subscription.blogDigitalSummary'),
      bullets: [
        t('subscription.blogDigitalBullet1'),
        t('subscription.blogDigitalBullet2'),
        t('subscription.blogDigitalBullet3'),
      ],
      href: '/digital-inspection-checklists',
      cta: t('subscription.blogDigitalCta'),
    },
    {
      id: 'spreadsheets',
      title: t('subscription.blogSpreadsheetTitle'),
      heading: t('subscription.blogSpreadsheetHeading'),
      summary: t('subscription.blogSpreadsheetSummary'),
      bullets: [
        t('subscription.blogSpreadsheetBullet1'),
        t('subscription.blogSpreadsheetBullet2'),
        t('subscription.blogSpreadsheetBullet3'),
      ],
      href: '/why-spreadsheets-fail-inspections',
      cta: t('subscription.blogSpreadsheetCta'),
    },
  ];

  const activeBlog = blogTabs.find((tab) => tab.id === activeBlogTab) || blogTabs[0];

  useEffect(() => {
    // Check if splash has been seen in this session
    const hasSeenSplash = sessionStorage.getItem('bmc:splashSeen');
    if (hasSeenSplash) {
      setShowSplash(false);
    }
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    const metaDescription = document.querySelector('meta[name="description"]') || document.createElement('meta');
    const previousDescription = metaDescription.getAttribute('content');
    metaDescription.setAttribute('name', 'description');
    metaDescription.setAttribute(
      'content',
      'BuildMyChecks is digital inspection, checklist and compliance software with mobile checks, split workflows, policy management, AI policy search, Knowledge Hub training records and audit-ready reports.'
    );
    if (!metaDescription.parentNode) document.head.appendChild(metaDescription);
    document.title = 'BuildMyChecks | Digital Inspection Software, AI Policy Search & Compliance Knowledge Hub';

    return () => {
      document.title = previousTitle;
      if (previousDescription) {
        metaDescription.setAttribute('content', previousDescription);
      }
    };
  }, []);

  const handleSplashFinish = () => {
    sessionStorage.setItem('bmc:splashSeen', '1');
    setShowSplash(false);
  };

  if (showSplash) {
    return <SplashScreen onFinish={handleSplashFinish} mode="on" />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50 to-gray-200">
      <Navbar />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          <h1 className="text-5xl font-extrabold text-gray-900 mb-6">
            {t('subscription.heroTitle')}
          </h1>
          <h2 className="text-2xl text-gray-700 mb-4 font-semibold">
            {t('subscription.heroSubtitle')}
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            {t('subscription.heroDescription')}
          </p>
          <button
            onClick={() => navigate('/register')}
            className="bg-primary hover:bg-red-700 text-white px-8 py-4 rounded-lg text-lg font-semibold shadow-lg"
            aria-label={t('subscription.startFreeTrial')}
          >
            {t('subscription.startFreeTrial')}
          </button>

          <div className="mt-10 flex flex-col items-center gap-4">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <a 
                href="https://apps.apple.com/app/id6758455875" 
                className="block transition-transform hover:scale-105"
                aria-label={t('subscription.downloadAppStore')}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img 
                  src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&amp;releaseDate=1234567890" 
                  alt={t('subscription.downloadAppStore')} 
                  className="h-12"
                />
              </a>
              {!isIOS && (
                <a 
                  href="https://play.google.com/store/apps/details?id=com.buildmychecks.mobile" 
                  className="block transition-transform hover:scale-105"
                  aria-label={t('subscription.getOnGooglePlay')}
                >
                  <img 
                    src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" 
                    alt={t('subscription.getOnGooglePlay')} 
                    className="h-[72px]"
                  />
                </a>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {isIOS ? t('subscription.availableAppStore') : t('subscription.availableAppStoreAndPlay')}
            </p>
          </div>
        </div>
      </section>

      {/* Live Demo Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 bg-gradient-to-br from-gray-50 via-emerald-50 to-gray-200">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('subscription.tryDemo')}</h2>
          <p className="text-lg text-gray-600">{t('subscription.demoDescription')}</p>
        </div>
        <InteractiveDemo />
        <div className="text-center mt-8">
          <button
            onClick={() => navigate('/register')}
            className="bg-primary hover:bg-red-700 text-white px-8 py-3 rounded-lg text-lg font-semibold shadow-lg"
          >
            {t('subscription.tryFreeNoCreditCard')}
          </button>
        </div>
      </section>

      {/* Knowledge Hub Feature Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="rounded-[2rem] bg-slate-950 text-white overflow-hidden shadow-2xl">
          <div className="grid lg:grid-cols-[0.95fr_1.05fr]">
            <div className="p-8 lg:p-12 bg-[radial-gradient(circle_at_top_left,#22d3ee55,transparent_35%),linear-gradient(135deg,#020617,#0f172a)]">
              <div className="flex flex-col sm:flex-row sm:items-start gap-5 mb-6">
                <div className="cogz-assistant-orb">
                  <img src={cogzImage} alt="Cogz AI compliance assistant for BuildMyChecks policy search and Knowledge Hub" />
                </div>
                <div>
                  <p className="uppercase tracking-[0.22em] text-cyan-300 text-sm font-semibold mb-4">
                    {t('subscription.knowledgeHubMarketing.landing.eyebrow', 'Cogz AI compliance assistant')}
                  </p>
                  <h2 className="text-4xl font-extrabold mb-5">
                    {t('subscription.knowledgeHubMarketing.landing.cogzTitle', 'Meet Cogz: AI policy search and Knowledge Hub for audit-ready compliance')}
                  </h2>
                </div>
              </div>
              <p className="text-lg text-slate-300 mb-6">
                {t('subscription.knowledgeHubMarketing.landing.cogzDescription', 'Cogz helps BuildMyChecks teams search policies and procedures with natural language, track staff sign-off, record assessment evidence and surface answers from approved policy summaries without rescanning large documents.')}
              </p>
              <p className="text-sm text-cyan-100/90 mb-6">
                {t('subscription.knowledgeHubMarketing.landing.cogzSeoText', 'Use Cogz as an AI compliance assistant for digital inspection software, controlled documents, policy acknowledgements, training records, assessment evidence and audit-ready reporting.')}
              </p>
              <button
                onClick={() => navigate('/features')}
                className="bg-cyan-300 hover:bg-cyan-200 text-slate-950 px-6 py-3 rounded-xl font-bold shadow-lg"
              >
                {t('subscription.knowledgeHubMarketing.landing.cogzCta', 'Explore Cogz and Knowledge Hub features')}
              </button>
            </div>
            <div className="p-8 lg:p-12 bg-white text-slate-950">
              <div className="grid sm:grid-cols-2 gap-5">
                <article className="rounded-2xl border border-gray-200 p-5">
                  <h3 className="font-bold text-xl mb-2">{t('subscription.knowledgeHubMarketing.landing.askTitle', 'Ask policy questions')}</h3>
                  <p className="text-gray-600">{t('subscription.knowledgeHubMarketing.landing.askText', 'Use natural language to search controlled documents and get answers from generated policy summaries with source policy versions.')}</p>
                </article>
                <article className="rounded-2xl border border-gray-200 p-5">
                  <h3 className="font-bold text-xl mb-2">{t('subscription.knowledgeHubMarketing.landing.trainingTitle', 'Training record dashboard')}</h3>
                  <p className="text-gray-600">{t('subscription.knowledgeHubMarketing.landing.trainingText', 'Track completed, pending, overdue and failed policy acknowledgements for users, roles and groups.')}</p>
                </article>
                <article className="rounded-2xl border border-gray-200 p-5">
                  <h3 className="font-bold text-xl mb-2">{t('subscription.knowledgeHubMarketing.landing.evidenceTitle', 'Assessment evidence')}</h3>
                  <p className="text-gray-600">{t('subscription.knowledgeHubMarketing.landing.evidenceText', 'Store answers, pass percentages, digital signatures, timestamps, device information and IP metadata.')}</p>
                </article>
                <article className="rounded-2xl border border-gray-200 p-5">
                  <h3 className="font-bold text-xl mb-2">{t('subscription.knowledgeHubMarketing.landing.suggestedTitle', 'Suggested documents')}</h3>
                  <p className="text-gray-600">{t('subscription.knowledgeHubMarketing.landing.suggestedText', 'Recommend relevant policies based on user role, department, group assignment and outstanding compliance actions.')}</p>
                </article>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 bg-white rounded-xl shadow-xl my-12" itemScope itemType="https://schema.org/Product">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4" itemProp="name">{t('subscription.simplePricing')}</h2>
          <p className="text-lg text-gray-600" itemProp="description">{t('subscription.noPricingHidden')}</p>
        </div>
        
        <div className="max-w-md mx-auto bg-gradient-to-br from-primary to-red-700 text-white rounded-2xl p-8 shadow-2xl">
          <h4 className="text-2xl font-bold mb-4">{t('subscription.professionalPlan')}</h4>
          <div className="mb-6">
            <span className="text-5xl font-extrabold">{t('subscription.pricePerUser')}</span>
            <span className="text-xl">{t('subscription.perUserMonth')}</span>
          </div>
          <ul className="space-y-3 mb-8">
            <li className="flex items-center">
              <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {t('subscription.unlimitedChecklists')}
            </li>
            <li className="flex items-center">
              <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {t('subscription.unlimitedSubmissions')}
            </li>
            <li className="flex items-center">
              <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {t('subscription.roleBasedAccess')}
            </li>
            <li className="flex items-center">
              <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {t('subscription.smartDraftSystem')}
            </li>
            <li className="flex items-center">
              <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {t('subscription.photoSignature')}
            </li>
            <li className="flex items-center">
              <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {t('subscription.pdfExports')}
            </li>
            <li className="flex items-center">
              <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {t('subscription.prioritySupport')}
            </li>
          </ul>
          <button
            onClick={() => navigate('/register')}
            className="w-full bg-white text-primary hover:bg-gray-100 px-6 py-3 rounded-lg font-semibold shadow"
          >
            {t('nav.getStarted')}
          </button>
        </div>

        {/* BuildMyShifts Pricing Card */}
        <div className="max-w-md mx-auto mt-12">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              {t('subscription.bms.partOfPlatform')}
            </div>
          </div>
          <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white rounded-2xl p-8 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <img src="https://buildmyshifts.com/logo.png" alt="BuildMyShifts" className="w-8 h-8 rounded-lg" />
              <h4 className="text-2xl font-bold">{t('subscription.bms.planName')}</h4>
            </div>
            <div className="mb-6">
              <span className="text-5xl font-extrabold">{t('subscription.bms.price')}</span>
              <span className="text-xl">{t('subscription.bms.perMonth')}</span>
            </div>
            <ul className="space-y-3 mb-6">
              {['unlimitedRotas', 'aiAutoFill', 'timeClock', 'payrollExport', 'shiftSwapBoard', 'staffProfiles', 'accreditations'].map((key) => (
                <li key={key} className="flex items-center">
                  <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {t(`subscription.bms.${key}`)}
                </li>
              ))}
            </ul>
            <div className="bg-white/10 rounded-lg p-3 mb-6 text-center">
              <span className="text-sm font-medium">{t('subscription.bms.bundleMessage')}</span>
            </div>
            <a
              href="https://buildmyshifts.com/register"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full block bg-white text-blue-700 hover:bg-gray-100 px-6 py-3 rounded-lg font-semibold shadow text-center"
            >
              {t('subscription.bms.startTrial')}
            </a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('subscription.powerfulFeatures')}</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t('subscription.featuresDescription')}
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          <article className="bg-white p-6 rounded-xl shadow-lg border-2 border-cyan-100">
            <div className="text-4xl mb-4" aria-hidden="true">AI</div>
            <h3 className="text-xl font-bold mb-2">AI Knowledge Hub</h3>
            <p className="text-gray-600">
              Search policies and procedures with natural language, ask questions from generated policy summaries, and keep audit-ready training records in one compliance knowledge hub.
            </p>
          </article>

          <article className="bg-white p-6 rounded-xl shadow-lg">
            <div className="text-4xl mb-4" aria-hidden="true">📝</div>
            <h3 className="text-xl font-bold mb-2">{t('subscription.customChecklists')}</h3>
            <p className="text-gray-600">{t('subscription.customChecklistsDesc')}</p>
          </article>
          
          <article className="bg-white p-6 rounded-xl shadow-lg">
            <div className="text-4xl mb-4" aria-hidden="true">👥</div>
            <h3 className="text-xl font-bold mb-2">{t('subscription.teamManagement')}</h3>
            <p className="text-gray-600">{t('subscription.teamManagementDesc')}</p>
          </article>
          
          <article className="bg-white p-6 rounded-xl shadow-lg">
            <div className="text-4xl mb-4" aria-hidden="true">💾</div>
            <h3 className="text-xl font-bold mb-2">{t('subscription.smartDraft')}</h3>
            <p className="text-gray-600">{t('subscription.smartDraftDesc')}</p>
          </article>
          
          <article className="bg-white p-6 rounded-xl shadow-lg">
            <div className="text-4xl mb-4" aria-hidden="true">📊</div>
            <h3 className="text-xl font-bold mb-2">{t('subscription.reportsAnalytics')}</h3>
            <p className="text-gray-600">{t('subscription.reportsAnalyticsDesc')}</p>
          </article>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8 mt-8">
          <article className="bg-white p-6 rounded-xl shadow-lg">
            <div className="text-4xl mb-4" aria-hidden="true">📱</div>
            <h3 className="text-xl font-bold mb-2">{t('subscription.mobileFirst')}</h3>
            <p className="text-gray-600">{t('subscription.mobileFirstDesc')}</p>
          </article>
          
          <article className="bg-white p-6 rounded-xl shadow-lg">
            <div className="text-4xl mb-4" aria-hidden="true">✅</div>
            <h3 className="text-xl font-bold mb-2">{t('subscription.approvalWorkflows')}</h3>
            <p className="text-gray-600">{t('subscription.approvalWorkflowsDesc')}</p>
          </article>
          
          <article className="bg-white p-6 rounded-xl shadow-lg">
            <div className="text-4xl mb-4" aria-hidden="true">📅</div>
            <h3 className="text-xl font-bold mb-2">{t('subscription.scheduledInspections')}</h3>
            <p className="text-gray-600">{t('subscription.scheduledInspectionsDesc')}</p>
          </article>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8 mt-8">
          <article className="bg-white p-6 rounded-xl shadow-lg">
            <div className="text-4xl mb-4" aria-hidden="true">📷</div>
            <h3 className="text-xl font-bold mb-2">{t('subscription.photoSignatureCapture')}</h3>
            <p className="text-gray-600">{t('subscription.photoSignatureCaptureDesc')}</p>
          </article>
          
          <article className="bg-white p-6 rounded-xl shadow-lg">
            <div className="text-4xl mb-4" aria-hidden="true">🔒</div>
            <h3 className="text-xl font-bold mb-2">{t('subscription.secureCompliant')}</h3>
            <p className="text-gray-600">{t('subscription.secureCompliantDesc')}</p>
          </article>
        </div>
      </section>

      {/* Blog Section */}
      <section id="blog" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 bg-white rounded-xl shadow-xl my-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('subscription.blogResources')}</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t('subscription.blogDescription')}
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {blogTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveBlogTab(tab.id)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors border ${
                  tab.id === activeBlogTab
                    ? 'bg-primary border-primary text-white'
                    : 'bg-white border-gray-300 text-gray-700 hover:border-primary hover:text-primary'
                }`}
                aria-pressed={tab.id === activeBlogTab}
              >
                {tab.title}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-start">
          <article className="bg-gradient-to-br from-gray-50 via-emerald-50 to-white border border-emerald-100 rounded-2xl shadow-lg p-8">
            <p className="text-sm font-semibold uppercase tracking-wide text-emerald-700 mb-3">{activeBlog.title} {t('subscription.spotlight')}</p>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">{activeBlog.heading}</h3>
            <p className="text-gray-700 mb-5">{activeBlog.summary}</p>
            <ul className="space-y-2 text-gray-800 mb-6">
              {activeBlog.bullets.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-1 h-2 w-2 rounded-full bg-primary" aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <a
              href={activeBlog.href}
              className="inline-flex items-center gap-2 text-primary font-semibold hover:text-red-700"
              aria-label={`${activeBlog.cta} ${t('subscription.onBlogLabel')}`}
            >
              {activeBlog.cta}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </article>

          <div className="grid sm:grid-cols-2 gap-4">
            {blogTabs
              .filter((tab) => tab.id !== activeBlogTab)
              .map((tab) => (
                <article key={tab.id} className="bg-white border border-gray-200 rounded-xl shadow p-5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-2">{tab.title}</p>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{tab.heading}</h4>
                  <p className="text-sm text-gray-700 mb-4">{tab.summary}</p>
                  <a
                    href={tab.href}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-red-700"
                    aria-label={`${tab.cta} ${t('subscription.onBlogLabel')}`}
                  >
                    {tab.cta}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                </article>
              ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industries" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 bg-gray-100 rounded-xl my-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('subscription.trustedByUK')}</h2>
          <p className="text-lg text-gray-600">{t('subscription.digitalInspectionSoftware')}</p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white p-4 rounded-lg shadow text-center">
            <h3 className="font-bold text-gray-900 mb-2">{t('subscription.industryConstruction')}</h3>
            <p className="text-sm text-gray-600">{t('subscription.industryConstructionDesc')}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow text-center">
            <h3 className="font-bold text-gray-900 mb-2">{t('subscription.industryManufacturing')}</h3>
            <p className="text-sm text-gray-600">{t('subscription.industryManufacturingDesc')}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow text-center">
            <h3 className="font-bold text-gray-900 mb-2">{t('subscription.industryHealthcare')}</h3>
            <p className="text-sm text-gray-600">{t('subscription.industryHealthcareDesc')}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow text-center">
            <h3 className="font-bold text-gray-900 mb-2">{t('subscription.industryHospitality')}</h3>
            <p className="text-sm text-gray-600">{t('subscription.industryHospitalityDesc')}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow text-center">
            <h3 className="font-bold text-gray-900 mb-2">{t('subscription.industryFacilities')}</h3>
            <p className="text-sm text-gray-600">{t('subscription.industryFacilitiesDesc')}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow text-center">
            <h3 className="font-bold text-gray-900 mb-2">{t('subscription.industryTransport')}</h3>
            <p className="text-sm text-gray-600">{t('subscription.industryTransportDesc')}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow text-center">
            <h3 className="font-bold text-gray-900 mb-2">{t('subscription.industryEducation')}</h3>
            <p className="text-sm text-gray-600">{t('subscription.industryEducationDesc')}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow text-center">
            <h3 className="font-bold text-gray-900 mb-2">{t('subscription.industryProperty')}</h3>
            <p className="text-sm text-gray-600">{t('subscription.industryPropertyDesc')}</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-lg font-bold mb-4">{t('footer.brandName')}</h3>
              <p className="text-gray-400 text-sm">{t('footer.description')}</p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">{t('footer.product')}</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/features')} className="text-gray-400 hover:text-white">{t('nav.features')}</button></li>
                <li><button onClick={() => navigate('/pricing')} className="text-gray-400 hover:text-white">{t('nav.pricing')}</button></li>
                <li><a href="#industries" className="text-gray-400 hover:text-white">{t('footer.industries')}</a></li>
                <li><button onClick={() => navigate('/register')} className="text-gray-400 hover:text-white">{t('subscription.startFreeTrial')}</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">{t('footer.company')}</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/contact')} className="text-gray-400 hover:text-white">{t('nav.contact')}</button></li>
                <li><button onClick={() => navigate('/privacy')} className="text-gray-400 hover:text-white">{t('footer.privacyPolicy')}</button></li>
                <li><button onClick={() => navigate('/terms')} className="text-gray-400 hover:text-white">{t('footer.termsOfService')}</button></li>
                <li><button onClick={() => navigate('/refund-policy')} className="text-gray-400 hover:text-white">{t('footer.refundPolicy')}</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">{t('nav.contact')}</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>{t('footer.emailSupport')}</li>
                <li>{t('footer.emailSales')}</li>
                <li className="pt-4">
                  <BuildMyChecksSocialLinks />
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-400">
            <p>{t('footer.copyright')}</p>
            <p className="mt-2">{t('footer.tagline')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ----------------------------------------------------------------------------------------------
//  Build My Checks (c) 2025 Gareth Morgan - BuildMyChecks.co.uk
//  Solely designed, developed, and maintained by Gareth Morgan.
//  All intellectual property rights reserved.
//  No liability is accepted for unauthorised use, modification, deployment, or maintenance.
// ----------------------------------------------------------------------------------------------
